// src/components/Admin/SnackableCourse/StagedCoursePreview.tsx

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  List,
  ListItem,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Article,
  Quiz,
  Forum,
  Feedback,
  Description,
} from '@mui/icons-material';
import {
  SnackableCourseData,
  SnackablePage,
  SnackableQuiz,
  SnackableForum,
  SnackableResource,
} from '../../../types/moodleTypes';
import { ContentBlockRenderer } from './renderers/ContentBlockRenderer';

interface Props {
  course: SnackableCourseData | any; // Allow flexible structure
  deploymentState: 'draft' | 'staging';
}

export const StagedCoursePreview: React.FC<Props> = ({
                                                       course,
                                                       deploymentState,
                                                     }) => {
  const [expandedSection, setExpandedSection] = useState<number | false>(0);
  const [expandedPage, setExpandedPage] = useState<string | false>(false);

  // ✅ FIXED: Extract data from nested structures
  const courseData = course.staged_course || course.deployed_course || course;

  const fullname = courseData.fullname || courseData.title || 'Untitled Course';
  const shortname = courseData.shortname || '';
  const summary = courseData.summary || '';
  const sections = courseData.sections || [];
  const pages = courseData.pages || [];
  const quizzes = courseData.quizzes || [];
  const forums = courseData.forums || [];
  const resources = courseData.resources || [];
  const feedback = courseData.feedback || [];

  const renderPage = (page: SnackablePage, pageIndex: number) => {
    const pageKey = `page-${pageIndex}`;

    return (
      <Accordion
        key={pageKey}
        expanded={expandedPage === pageKey}
        onChange={() =>
          setExpandedPage(expandedPage === pageKey ? false : pageKey)
        }
        sx={{ mb: 1, bgcolor: 'grey.50' }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Article fontSize="small" color="primary" />
            <Typography variant="subtitle2">{page.name}</Typography>
            <Chip
              size="small"
              label={`${page.content?.length || 0} blocks`}
              variant="outlined"
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Paper sx={{ p: 3, bgcolor: 'white' }}>
            {page.content?.map((block, blockIndex) => (
              <ContentBlockRenderer
                key={blockIndex}
                block={block}
                index={blockIndex}
              />
            )) || <Typography color="text.secondary">No content</Typography>}
          </Paper>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderQuiz = (quiz: SnackableQuiz, quizIndex: number) => {
    return (
      <Paper
        key={quizIndex}
        variant="outlined"
        sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Quiz color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            {quiz.name}
          </Typography>
          <Chip size="small" label={`${quiz.questions?.length || 0} questions`} />
        </Box>

        {quiz.intro && quiz.intro.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            {quiz.intro.map((block, idx) => (
              <ContentBlockRenderer key={idx} block={block} index={idx} />
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {quiz.questions?.map((q, idx) => (
          <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" gutterBottom>
              Question {idx + 1}: {q.name}
            </Typography>
            <Typography variant="body2" paragraph>
              {q.questiontext}
            </Typography>

            <List dense>
              {q.choices?.map((choice, choiceIdx) => (
                <ListItem key={choiceIdx} sx={{ pl: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: choiceIdx === q.correctanswer ? 600 : 400,
                      color:
                        choiceIdx === q.correctanswer
                          ? 'success.main'
                          : 'text.primary',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: `${String.fromCharCode(65 + choiceIdx)}. ${choice}${
                        choiceIdx === q.correctanswer ? ' ✓' : ''
                      }`,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ))}
      </Paper>
    );
  };

  const renderForum = (forum: SnackableForum) => {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'secondary.light' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Forum color="secondary" />
          <Typography variant="subtitle2" fontWeight={600}>
            {forum.name}
          </Typography>
        </Box>
        {forum.intro && forum.intro.length > 0 && (
          <Box>
            {forum.intro.map((block, idx) => (
              <ContentBlockRenderer key={idx} block={block} index={idx} />
            ))}
          </Box>
        )}
      </Paper>
    );
  };

  const renderResources = (resources: SnackableResource[]) => {
    if (!resources || resources.length === 0) return null;

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Description />
          <Typography variant="subtitle2" fontWeight={600}>
            Resources ({resources.length})
          </Typography>
        </Box>
        <List dense>
          {resources.map((resource, idx) => (
            <ListItem key={idx}>
              <Typography variant="body2">
                <strong>{resource.name}:</strong>{' '}
                <a
                  href={resource.reference}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resource.reference}
                </a>
              </Typography>
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  };

  const getSectionContent = (sectionIndex: number) => {
    return {
      pages: pages.filter((p) => p.section === sectionIndex) || [],
      quiz: quizzes.find((q) => q.section === sectionIndex),
      forum: forums.find((f) => f.section === sectionIndex),
      resources: resources.filter((r) => r.section === sectionIndex) || [],
      feedback: feedback.find((f) => f.section === sectionIndex),
    };
  };

  return (
    <Box>
      {/* Deployment Status Banner */}
      <Alert
        severity={deploymentState === 'draft' ? 'info' : 'warning'}
        sx={{ mb: 3 }}
      >
        <strong>
          {deploymentState === 'draft' ? 'Draft Mode' : 'Staged (Not Deployed)'}
        </strong>{' '}
        – Showing structured JSON content.{' '}
        {deploymentState === 'staging' &&
          'Deploy to Moodle to preview live content.'}
      </Alert>

      {/* Course Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {fullname}
        </Typography>
        {shortname && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Short Name:</strong> {shortname}
          </Typography>
        )}
        {summary && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            {summary}
          </Typography>
        )}
      </Paper>

      {/* Course Statistics */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Course Structure:
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label={`${sections.length} sections`} size="small" />
          <Chip label={`${pages.length} pages`} size="small" />
          <Chip
            label={`${quizzes.length} quizzes`}
            size="small"
            color="primary"
          />
          <Chip
            label={`${forums.length} forums`}
            size="small"
            color="secondary"
          />
          {resources.length > 0 && (
            <Chip label={`${resources.length} resources`} size="small" />
          )}
        </Box>
      </Paper>

      {/* Sections */}
      <Typography variant="h6" gutterBottom>
        Sections:
      </Typography>

      {sections.map((section, sectionIndex) => {
        const sectionContent = getSectionContent(sectionIndex);

        return (
          <Accordion
            key={sectionIndex}
            expanded={expandedSection === sectionIndex}
            onChange={() =>
              setExpandedSection(
                expandedSection === sectionIndex ? false : sectionIndex
              )
            }
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="subtitle1" fontWeight={600}>
                  {section.name}
                </Typography>
                <Box display="flex" gap={1}>
                  {sectionContent.pages.length > 0 && (
                    <Chip
                      size="small"
                      icon={<Article />}
                      label={`${sectionContent.pages.length} pages`}
                    />
                  )}
                  {sectionContent.quiz && (
                    <Chip
                      size="small"
                      icon={<Quiz />}
                      label="Quiz"
                      color="primary"
                    />
                  )}
                  {sectionContent.forum && (
                    <Chip
                      size="small"
                      icon={<Forum />}
                      label="Forum"
                      color="secondary"
                    />
                  )}
                  {sectionContent.resources.length > 0 && (
                    <Chip
                      size="small"
                      icon={<Description />}
                      label={`${sectionContent.resources.length} resources`}
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {/* Section Summary */}
              {section.summary && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {section.summary}
                </Alert>
              )}

              {/* Pages */}
              {sectionContent.pages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    Pages ({sectionContent.pages.length})
                  </Typography>
                  {sectionContent.pages.map((page, pageIndex) =>
                    renderPage(page, pageIndex)
                  )}
                </Box>
              )}

              {/* Quiz */}
              {sectionContent.quiz &&
                renderQuiz(sectionContent.quiz, sectionIndex)}

              {/* Forum */}
              {sectionContent.forum && renderForum(sectionContent.forum)}

              {/* Resources */}
              {sectionContent.resources.length > 0 &&
                renderResources(sectionContent.resources)}

              {/* Feedback */}
              {sectionContent.feedback && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Feedback />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Course Feedback
                    </Typography>
                  </Box>
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};